﻿import { FC, ReactNode } from "react";
import { Box, Table, TableProps, Tbody, Th, Thead, Tr } from "@chakra-ui/react";
import { UseQueryResult } from "@tanstack/react-query";
import { TrLoading } from "../TrLoading";

interface TableResultProps {
    query: UseQueryResult;
    dataLength?: number; // in cases where the query.data is not an array send the count of objects here instead
    columns: Array<string>;
    children?: ReactNode;
    loading?: string;
    noData?: ReactNode;
}

/**
 * This component is a passthrough to the Table component that will display a loading state, no data state, or the children.
 */
export const TableResult: FC<TableResultProps & TableProps> = ({ query, dataLength, columns, children, loading, noData = "No Data Found.", ...tableProps }) => {
    let result: ReactNode = null;
    let displayAfter: ReactNode = null;

    if (query.isLoading) {
        if (loading) {
            result = loading;
        } else {
            result = <TrLoading colSpan={columns.length} />;
        }
    } else {
        result = children;

        // @ts-ignore
        if (query.data?.length === 0 || dataLength === 0) {
            if (typeof noData === "string") {
                displayAfter = (
                    <Box textAlign={"center"} py={"1rem"}>
                        {noData}
                    </Box>
                );
            } else {
                displayAfter = noData;
            }
        }

        if (query.error) {
            displayAfter = (
                <Box textAlign={"center"} py={"1rem"} color={"red.500"}>
                    Error Loading Data
                </Box>
            );
        }
    }

    return (
        <>
            <Table variant="simple" colorScheme={"gray"} size={"sm"} {...tableProps}>
                <Thead>
                    <Tr>{columns && columns.map((name, index) => <Th key={index}>{name}</Th>)}</Tr>
                </Thead>
                <Tbody>{result}</Tbody>
            </Table>
            {displayAfter}
        </>
    );
};
